<script setup>
import { ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import TasksAPI from 'kanban/api/tasks';
import { UPDATE_TASK } from 'kanban/store/mutation-types';

const props = defineProps({
  task: { type: Object, default: null },
  editable: { type: Boolean, default: true },
});
const emit = defineEmits(['updated']);
const { t } = useI18n();
const store = useStore();
const photos = ref([]);
const busy = ref(false);
const error = ref('');

watch(
  () => props.task,
  task => {
    photos.value = task?.patient_photos || [];
    error.value = '';
  },
  { immediate: true }
);

const save = async (contactId, file = null) => {
  if (busy.value) return;
  if (
    file &&
    (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type) ||
      file.size > 5 * 1024 * 1024)
  ) {
    error.value = t('KANBAN_PATIENT_PHOTO.INVALID');
    return;
  }
  busy.value = true;
  error.value = '';
  try {
    const { data } = await TasksAPI.patientPhoto(
      props.task.id,
      contactId,
      file
    );
    photos.value = data.patient_photos;
    if (store.hasModule('kanban')) store.commit(`kanban/${UPDATE_TASK}`, data);
    emit('updated', data);
  } catch {
    error.value = t('KANBAN_PATIENT_PHOTO.ERROR');
  } finally {
    busy.value = false;
  }
};

const upload = (event, contactId) => {
  const [file] = event.target.files;
  if (file) save(contactId, file);
  event.target.value = '';
};
</script>

<template>
  <div>
    <section
      v-if="task?.patient_photo_enabled && photos.length"
      class="flex flex-col gap-2"
    >
      <span v-if="editable" class="text-sm font-medium text-n-slate-12">
        {{ t('KANBAN_PATIENT_PHOTO.TITLE') }}
      </span>
      <div
        v-for="photo in photos"
        :key="photo.contact_id"
        class="flex items-center gap-3"
      >
        <img
          v-if="photo.url"
          :src="photo.url"
          :alt="photo.name"
          class="rounded-lg object-cover bg-n-slate-3 shrink-0"
          :class="editable ? 'size-24' : 'size-16'"
          loading="lazy"
        />
        <span
          v-else
          class="size-16 rounded-lg bg-n-slate-3 flex items-center justify-center text-n-slate-11"
        >
          <span class="i-lucide-user-round size-8" />
        </span>
        <div v-if="editable" class="flex flex-col gap-2 min-w-0">
          <span class="text-xs text-n-slate-11">{{ photo.name }}</span>
          <span class="text-xs text-n-slate-11">
            {{
              photo.custom
                ? t('KANBAN_PATIENT_PHOTO.CUSTOM')
                : t('KANBAN_PATIENT_PHOTO.DEFAULT')
            }}
          </span>
          <label class="cursor-pointer text-sm text-n-blue-11">
            {{ t('KANBAN_PATIENT_PHOTO.UPLOAD') }}
            <input
              type="file"
              accept="image/jpeg,image/png,image/webp"
              class="block w-full text-xs"
              :disabled="busy"
              @change="upload($event, photo.contact_id)"
            />
          </label>
          <button
            v-if="photo.custom"
            type="button"
            class="text-left text-xs text-n-blue-11"
            :disabled="busy"
            @click="save(photo.contact_id)"
          >
            {{ t('KANBAN_PATIENT_PHOTO.RESET') }}
          </button>
        </div>
      </div>
      <span v-if="busy" role="status" class="text-xs text-n-slate-11">{{
        t('KANBAN_PATIENT_PHOTO.SAVING')
      }}</span>
      <span v-if="error" role="alert" class="text-xs text-n-ruby-11">{{
        error
      }}</span>
    </section>
  </div>
</template>
